import React, { useCallback, useEffect, useRef, useState } from "react";
import asset from "../utils/assetUrl";

// Los .webm son VP9 + Opus, que el Safari del iPhone no decodifica: los
// testimonios no salian en iOS. Ahora cada uno tiene tambien un .mp4 en
// H.264 + AAC, que reproduce cualquier navegador, y va primero en la lista
// de <source> para que iOS no dependa de su soporte parcial de WebM.
//
// El .webm se queda detras como respaldo. Si algun dia hay que regenerar los
// mp4, el comando esta en docs/backend.md.
const testimonials = [
    { id: 1, webm: "/assets/videos/testimonio-1.webm", mp4: "/assets/videos/testimonio-1.mp4", poster: "/assets/videos/poster-1.jpg" },
    { id: 2, webm: "/assets/videos/testimonio-2.webm", mp4: "/assets/videos/testimonio-2.mp4", poster: "/assets/videos/poster-2.jpg" },
    { id: 3, webm: "/assets/videos/testimonio-3.webm", mp4: "/assets/videos/testimonio-3.mp4", poster: "/assets/videos/poster-3.jpg" },
    { id: 4, webm: "/assets/videos/testimonio-4.webm", mp4: "/assets/videos/testimonio-4.mp4", poster: "/assets/videos/poster-4.jpg" },
    { id: 5, webm: "/assets/videos/testimonio-5.webm", mp4: "/assets/videos/testimonio-5.mp4", poster: "/assets/videos/poster-5.jpg" },
    { id: 6, webm: "/assets/videos/testimonio-6.webm", mp4: "/assets/videos/testimonio-6.mp4", poster: "/assets/videos/poster-6.jpg" },
    { id: 7, webm: "/assets/videos/testimonio-7.webm", mp4: "/assets/videos/testimonio-7.mp4", poster: "/assets/videos/poster-7.jpg" },
    { id: 8, webm: "/assets/videos/testimonio-8.webm", mp4: "/assets/videos/testimonio-8.mp4", poster: "/assets/videos/poster-8.jpg" }
];

const gallery = [
    { src: "/assets/images/eventos/stands-instituciones.jpg", alt: "Asistentes visitando los stands de las instituciones educativas canadienses", size: "wide" },
    { src: "/assets/images/eventos/conferencia-auditorio.jpg", alt: "Conferencia sobre costo de vida en Canadá ante un auditorio lleno", size: "tall" },
    { src: "/assets/images/eventos/asesoria-olds-college.jpg", alt: "Asesoría uno a uno con el representante de Olds College", size: "" },
    { src: "/assets/images/eventos/conferencia-sait.jpg", alt: "Presentación de SAIT durante la conferencia", size: "" },
    { src: "/assets/images/eventos/asesoria-red-river.jpg", alt: "Atención personalizada en el stand de Red River College Polytechnic", size: "" },
    { src: "/assets/images/eventos/asistentes-bandera.jpg", alt: "Asistentes celebrando con la bandera de Canadá", size: "" }
];

function SocialProof() {
    const [activeId, setActiveId] = useState(null);
    const videoRefs = useRef({});

    // --- Carrusel: estado para saber si queda contenido a cada lado ---
    const scrollerRef = useRef(null);
    const [edges, setEdges] = useState({ left: false, right: false });
    const [hasScrolled, setHasScrolled] = useState(false);

    const updateEdges = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        // 2px de tolerancia: en zoom no entero scrollLeft no llega exacto al final.
        setEdges({
            left: el.scrollLeft > 2,
            right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2
        });
    }, []);

    useEffect(() => {
        updateEdges();
        window.addEventListener('resize', updateEdges);
        return () => window.removeEventListener('resize', updateEdges);
    }, [updateEdges]);

    const handleScroll = () => {
        updateEdges();
        if (!hasScrolled) setHasScrolled(true);
    };

    // Avanza el ancho de una tarjeta + el gap, leidos del DOM para no
    // duplicar aqui los valores del SCSS.
    const scrollByCard = (direction) => {
        const el = scrollerRef.current;
        if (!el) return;
        const card = el.querySelector('.proof__video');
        const gap = parseFloat(window.getComputedStyle(el).columnGap || '15') || 15;
        const step = card ? card.offsetWidth + gap : el.clientWidth * 0.8;
        el.scrollBy({ left: step * direction, behavior: 'smooth' });
    };

    const handlePlay = (id) => {
        const video = videoRefs.current[id];
        setActiveId(id);
        if (video && typeof video.play === "function") {
            const played = video.play();
            if (played && typeof played.catch === "function") {
                played.catch(() => {
                    /* el navegador bloqueó la reproducción: quedan los controles nativos */
                });
            }
        }
    };

    return (
        <>
            <div
                className={`proof__carousel ${edges.left ? 'has-prev' : ''} ${edges.right ? 'has-next' : ''}`}
            >
                <div
                    className="proof__videos"
                    role="list"
                    ref={scrollerRef}
                    onScroll={handleScroll}
                >
                {testimonials.map((testimonial) => (
                    <div className="proof__video" key={testimonial.id} role="listitem">
                        <video
                            ref={(el) => {
                                videoRefs.current[testimonial.id] = el;
                            }}
                            preload="none"
                            poster={asset(testimonial.poster)}
                            playsInline
                            controls={activeId === testimonial.id}
                            onPlay={() => setActiveId(testimonial.id)}
                        >
                            {testimonial.mp4 && <source src={asset(testimonial.mp4)} type="video/mp4" />}
                            <source src={asset(testimonial.webm)} type="video/webm" />
                            Tu navegador no puede reproducir este video.
                        </video>

                        {activeId !== testimonial.id && (
                            <button
                                type="button"
                                className="proof__play"
                                onClick={() => handlePlay(testimonial.id)}
                                aria-label={`Reproducir testimonio ${testimonial.id}`}
                            >
                                <span aria-hidden="true">▶</span>
                            </button>
                        )}
                    </div>
                ))}
                </div>

                <button
                    type="button"
                    className="proof__nav proof__nav--prev"
                    onClick={() => scrollByCard(-1)}
                    aria-label="Ver testimonios anteriores"
                    tabIndex={edges.left ? 0 : -1}
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path d="M15.5 4.5 8 12l7.5 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                <button
                    type="button"
                    className="proof__nav proof__nav--next"
                    onClick={() => scrollByCard(1)}
                    aria-label="Ver más testimonios"
                    tabIndex={edges.right ? 0 : -1}
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path d="M8.5 4.5 16 12l-7.5 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                {/* Pista tactil: se retira en cuanto el usuario desliza */}
                {edges.right && !hasScrolled && (
                    <span className="proof__hint" aria-hidden="true">
                        <svg viewBox="0 0 24 24" focusable="false">
                            <path d="M4 12h14M13 6.5l6 5.5-6 5.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Desliza para ver más
                    </span>
                )}
            </div>

            <div className="proof__gallery">
                {gallery.map((photo) => (
                    <figure className={`proof__photo ${photo.size}`} key={photo.src}>
                        <img src={asset(photo.src)} alt={photo.alt} loading="lazy" />
                    </figure>
                ))}
            </div>
        </>
    );
}

export default SocialProof;
